import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation, Link } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import { Brand, SiteHeader, SiteFooter } from "./components/SiteChrome";

// Load the map/chart libraries only when a visitor opens the monitor.
const AppPage = lazy(() => import("./pages/AppPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ModulesPage = lazy(() => import("./pages/ModulesPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));

function RouteMetadata() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const pages = {
      "/": [
        "AirVista — A clearer view of our environment",
        "Explore air quality, weather, river flow and soil-water conditions with source timestamps and clearly identified environmental models.",
      ],
      "/monitor": [
        "Environmental Monitor | AirVista",
        "Explore environmental data for a location across five modules, with provider timestamps, forecasts and historical context.",
      ],
      "/app": [
        "Environmental Monitor | AirVista",
        "Explore environmental data for a location across five modules, with provider timestamps, forecasts and historical context.",
      ],
      "/modules": [
        "Modules & Data Sources | AirVista",
        "Understand AirVista's five modules, environmental data providers, update schedules, model resolution and methodology.",
      ],
      "/about": [
        "About AirVista & Irfan Mahmood",
        "Learn about AirVista and its developer, Irfan Mahmood. Environmental context informed by remote sensing and geospatial expertise.",
      ],
      "/privacy": [
        "Privacy & Your Data | AirVista",
        "How AirVista uses location searches, saved places and shared links.",
      ],
    };
    const [title, description] = pages[pathname] || [
      "Page not found | AirVista",
      "Find your way back to AirVista's environmental monitor.",
    ];
    document.title = title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", description);
    for (const prefix of ["og", "twitter"]) {
      const attr = prefix === "og" ? "property" : "name";
      document
        .querySelector(`meta[${attr}="${prefix}:title"]`)
        ?.setAttribute("content", title);
      document
        .querySelector(`meta[${attr}="${prefix}:description"]`)
        ?.setAttribute("content", description);
    }
    const url = `${window.location.origin}${pathname === "/app" ? "/monitor" : pathname}`;
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", url);
    document
      .querySelector('meta[property="og:url"]')
      ?.setAttribute("content", url);
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

class PageBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed)
      return (
        <div className="route-loading">
          <Brand />
          <p>This page could not load. Please try again.</p>
          <button
            className="primary-button"
            onClick={() => window.location.reload()}
          >
            Reload AirVista
          </button>
        </div>
      );
    return this.props.children;
  }
}

function NotFound() {
  return (
    <div className="site-page">
      <SiteHeader />
      <main id="main-content" className="site-container not-found">
        <div className="eyebrow">404 · OFF THE MAP</div>
        <h1>Let’s find your way back.</h1>
        <p>This page doesn’t exist. There’s still a whole world to explore.</p>
        <Link className="primary-button" to="/">
          Back to AirVista
        </Link>
      </main>
      <SiteFooter />
    </div>
  );
}

export default function App() {
  return (
    <>
      <RouteMetadata />
      <PageBoundary>
        <Suspense
          fallback={
            <div className="route-loading" role="status">
              <Brand />
              <span>Loading your next perspective…</span>
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/app" element={<AppPage />} />
            <Route path="/monitor" element={<AppPage />} />
            <Route path="/modules" element={<ModulesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </PageBoundary>
    </>
  );
}
